/* @deprecated this component is used only in old checkout, should be removed when new checkout v2 is shipped. */
import type { FC } from "react";
import { memo, useEffect } from "react";

import { FormField, useFormContext } from "@ui/components/Form";
import { TextInput } from "@ui/components/Inputs/TextInput/TextInput";
import { Telephone } from "@ui/components/Telephone/Telephone";
import { useTranslations } from "@ui/providers/useTranslations";
import classnames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { applyNameOrder } from "@jti/fe-common";

import styles from "./ContactFields.module.scss";

export type ContactFieldsProps = {
  fieldName?(name: string): string;
  isPhoneWithoutDropdown?: boolean;
  isSingleNameField?: boolean;
  isPhoneRequired?: boolean;
};

export const ContactFields: FC<ContactFieldsProps> = memo(
  ({
    fieldName = (name: string) => name,
    isPhoneWithoutDropdown,
    isSingleNameField = false,
    isPhoneRequired = true,
  }) => {
    const { __ } = useTranslations();
    const { isReversedNameOrder } = useConfig();
    const { watch, setValue } = useFormContext();
    const telephonePrefix = "editPhone";
    const { value } = watch(`${telephonePrefix}.code`) || {};
    const phone = watch(`${telephonePrefix}.phone`);

    useEffect(() => {
      if (isPhoneWithoutDropdown && phone) {
        setValue(fieldName("telephone"), phone);
      } else if (value && phone) {
        setValue(fieldName("telephone"), `${value}${phone}`);
      }
    }, [value, phone]);

    const lasFieldNameText = __(isSingleNameField ? "contact_info.name" : "contact_info.lastname");

    const firstNameField = (
      <FormField
        id={fieldName("firstname")}
        name={fieldName("firstname")}
        label={__("contact_info.first_name")}
        showCustomError
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            id={fieldName("firstname")}
            data-testid="contactFirstnameInput"
            placeholder={__("contact_info.first_name")}
            autoComplete="off"
            isError={fieldState.invalid}
            errorText={fieldState.error?.message || ""}
          />
        )}
        validation={{ required: __("form_validation.field_required") }}
      />
    );

    const lastNameField = (
      <FormField
        id={fieldName("lastname")}
        name={fieldName("lastname")}
        label={lasFieldNameText}
        showCustomError
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            id={fieldName("lastname")}
            data-testid="contactLastnameInput"
            placeholder={lasFieldNameText}
            autoComplete="off"
            isError={fieldState.invalid}
            errorText={fieldState.error?.message || ""}
          />
        )}
        validation={{ required: __("form_validation.field_required") }}
      />
    );

    const [firstOrdered, lastOrdered] = applyNameOrder(firstNameField, lastNameField, isReversedNameOrder);

    return (
      <div
        className={classnames(styles.fields, { [styles.isSingleNameField]: isSingleNameField })}
        data-testid="contactForm"
      >
        {isSingleNameField ? (
          lastNameField
        ) : (
          <>
            {firstOrdered}
            {lastOrdered}
          </>
        )}
        <Telephone
          isWithoutDropdown={isPhoneWithoutDropdown}
          fieldName={(name) => `${telephonePrefix}.${name}`}
          isRequired={isPhoneRequired}
        />
      </div>
    );
  }
);
